import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

/**
 * Reusable order detail dialog. Shows header, timestamps, restaurant, customer,
 * delivery, items table and payment breakdown. When `showFinancials` is true it
 * also renders the platform commission / net-to-restaurant split.
 * Used by Admin.jsx (already inline for now), RestaurantAdmin.jsx and Rider.jsx.
 */
const OrderDetailDialog = ({
  order,
  open,
  onOpenChange,
  showFinancials = true,
  showRiderPayout = false,
}) => {
  if (!order) return null;
  const sub = order.subtotal || 0;
  const discount = order.discount || 0;
  const deliveryFee = order.delivery_fee || 0;
  const commissionPct = order.commission_pct ?? 0.20;
  const commission = order.commission_amount ?? +(sub * commissionPct).toFixed(2);
  const netRestaurant = order.restaurant_net ?? +(sub - commission - discount).toFixed(2);
  const riderFixed = order.rider_commission_fixed ?? 2.5;
  const riderPct = order.rider_commission_pct ?? 0.03;
  const riderPayout = order.rider_commission_amount ?? +(riderFixed + sub * riderPct).toFixed(2);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="shared-order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono">Ordine #{order.order_number}</DialogTitle>
          {order.created_at && (
            <div className="text-xs text-navy/60 font-normal">
              Ordinato il <span className="font-semibold text-navy">{new Date(order.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}</span>
              {order.accepted_at && <> · Preso in carico alle <span className="font-semibold text-navy">{new Date(order.accepted_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span></>}
              {order.delivered_at && <> · Consegnato alle <span className="font-semibold text-teal">{new Date(order.delivered_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span></>}
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div className="bg-sand-50 p-3 rounded-xl">
              <div className="text-xs uppercase text-navy/50">Ristorante</div>
              <div className="font-heading font-bold text-navy">{order.restaurant_name}</div>
              <div className="text-xs text-navy/60 mt-0.5">Stato: <span className="font-semibold text-navy">{order.status}</span></div>
            </div>
            <div className="bg-sand-50 p-3 rounded-xl">
              <div className="text-xs uppercase text-navy/50">Cliente</div>
              <div className="font-heading font-bold text-navy">{order.customer?.first_name} {order.customer?.last_name}</div>
              <div className="text-xs text-navy/60">{order.customer?.phone} · {order.customer?.email}</div>
            </div>
            <div className="bg-sand-50 p-3 rounded-xl sm:col-span-2">
              <div className="text-xs uppercase text-navy/50">Consegna</div>
              <div className="text-sm text-navy">{order.delivery?.accommodation_type} · {order.delivery?.accommodation_name} {order.delivery?.room && `· Camera ${order.delivery.room}`}</div>
              <div className="text-xs text-navy/60">{order.delivery?.address}</div>
              {order.delivery?.instructions && <div className="text-xs text-navy/60 italic mt-1">"{order.delivery.instructions}"</div>}
              {order.assigned_rider_name && <div className="text-xs text-teal font-bold mt-1">Rider: {order.assigned_rider_name}</div>}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase text-navy/50 mb-2">Prodotti acquistati</div>
            <div className="border border-navy/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-sand-50 text-left text-xs uppercase text-navy/50">
                  <tr>
                    <th className="p-2">Qtà</th>
                    <th className="p-2">Prodotto</th>
                    <th className="p-2 text-right">Prezzo</th>
                    <th className="p-2 text-right">Subtot.</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map((it, idx) => (
                    <tr key={idx} className="border-t border-navy/5 align-top">
                      <td className="p-2 font-heading font-bold">×{it.qty}</td>
                      <td className="p-2">
                        <div className="font-medium text-navy">{it.name}</div>
                        {(it.addons?.length > 0) && (
                          <div className="text-xs text-navy/60 mt-1">
                            <span className="uppercase tracking-wide text-[10px] text-teal font-bold">Extra:</span>{" "}
                            {it.addons.map((a, i) => (
                              <span key={i}>{a.name}{a.price ? ` (+€${(+a.price).toFixed(2)})` : ""}{i < it.addons.length - 1 ? ", " : ""}</span>
                            ))}
                          </div>
                        )}
                        {(it.removed_ingredients?.length > 0) && (
                          <div className="text-xs text-navy/60 mt-0.5">
                            <span className="uppercase tracking-wide text-[10px] text-coral font-bold">Senza:</span>{" "}
                            {it.removed_ingredients.join(", ")}
                          </div>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono text-navy/70">€{(+it.price).toFixed(2)}</td>
                      <td className="p-2 text-right font-mono text-navy">€{(it.price * it.qty).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-navy/5 rounded-xl p-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-navy/70">Subtotale prodotti</span><span className="font-mono text-navy">€{sub.toFixed(2)}</span></div>
            {discount > 0 && <div className="flex justify-between"><span className="text-navy/70">Sconto {order.promo_code ? `(${order.promo_code})` : ""}</span><span className="font-mono text-coral">-€{discount.toFixed(2)}</span></div>}
            <div className="flex justify-between"><span className="text-navy/70">Costo di consegna</span><span className="font-mono text-navy">€{deliveryFee.toFixed(2)}</span></div>
            <div className="flex justify-between pt-2 border-t border-navy/10 font-heading font-bold">
              <span className="text-navy">Totale ordine (pagato dal cliente)</span>
              <span className="font-mono text-navy text-lg">€{(order.total || 0).toFixed(2)}</span>
            </div>
          </div>

          {showFinancials && (
            <div className="border-2 border-teal/30 bg-teal/5 rounded-xl p-4 space-y-1.5 text-sm">
              <div className="text-xs uppercase text-teal font-bold tracking-wide mb-1">Ripartizione piattaforma</div>
              <div className="flex justify-between"><span className="text-navy/70">Commissione piattaforma ({Math.round(commissionPct * 100)}% sul subtotale)</span><span className="font-mono text-coral font-bold">-€{commission.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-navy/70">Costo di consegna (trattenuto)</span><span className="font-mono text-navy">€{deliveryFee.toFixed(2)}</span></div>
              <div className="flex justify-between pt-2 border-t border-teal/20 font-heading font-bold">
                <span className="text-navy">Netto al ristorante</span>
                <span className="font-mono text-teal text-lg">€{netRestaurant.toFixed(2)}</span>
              </div>
            </div>
          )}

          {showRiderPayout && (
            <div className="border-2 border-coral/30 bg-coral/5 rounded-xl p-4 space-y-1.5 text-sm">
              <div className="text-xs uppercase text-coral font-bold tracking-wide mb-1">Compenso rider</div>
              <div className="flex justify-between"><span className="text-navy/70">Fisso</span><span className="font-mono">€{riderFixed.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-navy/70">{(riderPct * 100).toFixed(0)}% su €{sub.toFixed(2)}</span><span className="font-mono">€{(sub * riderPct).toFixed(2)}</span></div>
              <div className="flex justify-between pt-2 border-t border-coral/20 font-heading font-bold">
                <span className="text-navy">Compenso totale</span>
                <span className="font-mono text-coral text-lg">€{riderPayout.toFixed(2)}</span>
              </div>
              {order.rider_paid_at && <div className="text-xs text-teal font-bold">✓ Saldato il {order.rider_paid_at}</div>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailDialog;
